import { useState, useRef, useEffect } from "react";
import { openDocFile } from "@/lib/open-doc-file";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { folderPage } from "@/lib/folder-pages";
import { useIsPhone } from "@/hooks/use-is-phone";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  FolderOpen,
  ArrowLeft,
  Folder,
  FolderPlus,
  FileText,
  Upload,
  Download,
  Eye,
  Archive,
  Trash2,
  Plus,
  ChevronRight,
  ChevronDown,
  Globe,
  Users,
  Building2,
  EyeOff,
  Loader2,
  FileWarning,
  History,
  RotateCcw,
  Lock,
  Sparkles,
  Pencil,
  X,
  Check,
  UserPlus,
  CalendarIcon,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { QuizAssignmentSettings, DEFAULT_QUIZ_CHOICE, normalizeQuizChoice, type QuizChoice } from "@/components/admin/quiz-assignment-settings";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import { authClient } from "@/lib/auth-client";
import * as XLSX from "xlsx-js-style";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/admin/documents")({
  component: DocumentsPage,
});

// ── Types ──────────────────────────────────────────────────────────────────
interface DocCategory {
  id: string;
  name: string;
  description: string | null;
  scope: "company" | "division";
  divisionId: string | null;
  parentId: string | null;
  status: string;
}

interface SafetyDoc {
  id: string;
  title: string;
  description: string | null;
  categoryId: string;
  visibility: "everyone" | "divisions" | "users" | "none";
  currentVersionId: string | null;
  status: "active" | "archived" | "deleted";
  createdAt: string;
  updatedAt: string;
  hasQuiz?: boolean;
  hasQuizBank?: boolean;
}

interface DocVersion {
  id: string;
  versionNumber: number;
  status: "current" | "superseded";
  changeNote: string | null;
  uploadedAt: string;
}

interface DocDetail {
  doc: SafetyDoc;
  versions: { version: DocVersion; file: { fileName: string; sizeBytes: number } | null; uploader: { name: string } | null }[];
  visibilityRules: { targetType: string; targetId: string }[];
}

interface Division { id: string; name: string; }

// ── Helpers ────────────────────────────────────────────────────────────────
function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function VisibilityBadge({ visibility }: { visibility: string }) {
  const map: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
    everyone: { label: "Everyone", icon: <Globe className="h-3 w-3" />, cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    divisions: { label: "Divisions", icon: <Building2 className="h-3 w-3" />, cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    users: { label: "Specific users", icon: <Users className="h-3 w-3" />, cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
    none: { label: "No one", icon: <EyeOff className="h-3 w-3" />, cls: "bg-muted text-muted-foreground" },
  };
  const v = map[visibility] ?? map.none;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${v.cls}`}>
      {v.icon}{v.label}
    </span>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
function DocumentsPage() {
  const { data: session } = authClient.useSession();
  const user = session?.user;
  const qc = useQueryClient();
  const isSuperAdmin = user?.role === "admin";
  const isDivAdmin = user?.role === "div_admin";
  const isAdmin = isSuperAdmin || isDivAdmin;

  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"active" | "archived">("active");
  const [showNewCat, setShowNewCat] = useState(false);
  const [showNewDoc, setShowNewDoc] = useState(false);
  const [versionDocId, setVersionDocId] = useState<string | null>(null);
  const [deleteDocTarget, setDeleteDocTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleteDocConfirm, setDeleteDocConfirm] = useState<{ id: string; title: string } | null>(null);
  const [editDocTarget, setEditDocTarget] = useState<SafetyDoc | null>(null);
  const [editCatTarget, setEditCatTarget] = useState<DocCategory | null>(null);
  const [deleteCatTarget, setDeleteCatTarget] = useState<{ id: string; name: string } | null>(null);
  const [subfolderParent, setSubfolderParent] = useState<DocCategory | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [assignDocTarget, setAssignDocTarget] = useState<SafetyDoc | null>(null);
  const [quizDocTarget, setQuizDocTarget] = useState<SafetyDoc | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [bulkAssignDocs, setBulkAssignDocs] = useState<{ doc: SafetyDoc; category: string }[] | null>(null);

  // Categories
  const { data: catsData } = useQuery({
    queryKey: ["admin", "safety-doc-categories"],
    queryFn: () => apiGet<{ data: DocCategory[] }>("/api/admin/safety-docs/categories"),
  });
  const cats = catsData?.data ?? [];
  const navigate = useNavigate();
  /** Opens a folder, or its own page when it has one (Toolbox Meetings -> the toolbox library). */
  const isPhone = useIsPhone();
  const docsTopRef = useRef<HTMLDivElement>(null);
  const selectFolder = (id: string | null) => {
    const page = id ? folderPage(cats.find((c) => c.id === id)?.name ?? "") : null;
    if (page) {
      navigate({ to: page as "/toolbox/library", search: {} });
      return;
    }
    if (!isPhone || !id) {
      setSelectedCatId(id);
      return;
    }
    // Phone: it works like a file explorer, so a folder opens its own page and what opened is shown from the top
    setSelectedCatId(id);
    window.setTimeout(() => docsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  // Documents
  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ["admin", "safety-docs", selectedCatId, statusFilter],
    queryFn: () => {
      const p = new URLSearchParams({ status: statusFilter });
      if (selectedCatId) p.set("categoryId", selectedCatId);
      return apiGet<{ data: { doc: SafetyDoc; category: { name: string } | null }[] }>(`/api/admin/safety-docs?${p}`);
    },
    enabled: selectedCatId !== null,
  });
  const docs = docsData?.data ?? [];

  // Divisions (super admin only)
  const { data: divsData } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
    enabled: isSuperAdmin,
  });
  const divisions = divsData?.data ?? [];

  const archiveCatMut = useMutation({
    mutationFn: (id: string) => apiPost(`/api/admin/safety-docs/categories/${id}/archive`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "safety-doc-categories"] }); toast.success("Folder archived"); },
    onError: (e: Error) => toast.error(e.message),
  });

  async function commitDeleteCat(id: string) {
    try {
      await apiDelete(`/api/admin/safety-docs/categories/${id}`);
      qc.invalidateQueries({ queryKey: ["admin", "safety-doc-categories"] });
      if (selectedCatId === id) setSelectedCatId(null);
      toast.success("Folder deleted");
    } catch (e: any) { toast.error(e.message || "Failed to delete folder"); }
  }

  const seedDefaultsMut = useMutation({
    mutationFn: () => apiPost<{ created: number; message?: string }>("/api/admin/safety-docs/seed-defaults", {}),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "safety-doc-categories"] });
      toast.success(res.message ?? `${res.created} default folders created`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const archiveDocMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "archive" | "restore" }) =>
      apiPost(`/api/admin/safety-docs/${id}/${action}`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "safety-docs"] }); toast.success("Document updated"); },
    onError: (e: Error) => toast.error(e.message),
  });

  async function commitDeleteDoc(id: string) {
    try {
      await apiDelete(`/api/admin/safety-docs/${id}`);
      qc.invalidateQueries({ queryKey: ["admin", "safety-docs"] });
      toast.success("Document permanently deleted");
    } catch (e: any) { toast.error(e.message || "Failed to delete document"); }
  }

  const topLevelCats = cats.filter((c) => !c.parentId);
  const companyCats = topLevelCats.filter((c) => c.scope === "company");
  const divisionCats = topLevelCats.filter((c) => c.scope === "division");
  const selectedCat = cats.find((c) => c.id === selectedCatId);
  // The folders from the top down to the open one
  const folderPath: DocCategory[] = [];
  for (let c = selectedCat; c; c = c.parentId ? cats.find((x) => x.id === c!.parentId) : undefined) folderPath.unshift(c);

  function childrenOf(id: string) { return cats.filter((c) => c.parentId === id); }
  function toggleExpand(id: string) {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function fetchAndOpenDoc(docId: string, versionId: string, mode: "view" | "download") {
    try {
      await openDocFile(docId, versionId, mode);
    } catch (e: any) {
      toast.error(e.message || "Failed to open file");
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Safety Documents</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage SWPs, SJPs, safety plans, and other compliance documents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/signing-reports">
            <Button variant="outline" size="sm" className="gap-1.5">
              <ClipboardList className="h-4 w-4" />Signing Reports
            </Button>
          </Link>
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowNewCat(true)}>
                <Plus className="h-4 w-4 mr-1" />Folder
              </Button>
              <Button
                size="sm"
                className="bg-paris-red hover:bg-paris-red/90"
                onClick={() => setShowNewDoc(true)}
                disabled={!selectedCatId}
                title={!selectedCatId ? "Select a folder first" : undefined}
              >
                <Upload className="h-4 w-4 mr-1" />Upload Doc
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Phone: one column (open folder's documents first, folder tree below).
          Desktop: tree | divider | documents. */}
      <div className="grid grid-cols-1 min-h-[60vh] md:[grid-template-columns:21rem_1px_1fr]">
        {/* Left: Category tree */}
        <div className="hidden space-y-1 overflow-y-auto md:block md:pr-2">
          <button
            onClick={() => setSelectedCatId(null)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
              selectedCatId === null ? "bg-paris-red/10 text-paris-red-text font-medium" : "hover:bg-muted text-muted-foreground"
            }`}
          >
            <FolderOpen className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">All Folders</span>
          </button>

          {companyCats.length > 0 && (
            <div className="pt-1">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Company</p>
              {companyCats.map((cat) => (
                <CatTreeRow
                  key={cat.id}
                  cat={cat}
                  children={childrenOf(cat.id)}
                  allCats={cats}
                  selected={selectedCatId}
                  expanded={expandedCats}
                  onSelect={selectFolder}
                  onToggleExpand={toggleExpand}
                  onEdit={setEditCatTarget}
                  onDelete={(c) => setDeleteCatTarget({ id: c.id, name: c.name })}
                  onAddSubfolder={setSubfolderParent}
                  onArchive={(id) => archiveCatMut.mutate(id)}
                  canModify={isSuperAdmin}
                  indent={0}
                />
              ))}
            </div>
          )}

          {divisionCats.length > 0 && (
            <div className="pt-1">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Division</p>
              {divisionCats.map((cat) => (
                <CatTreeRow
                  key={cat.id}
                  cat={cat}
                  children={childrenOf(cat.id)}
                  allCats={cats}
                  selected={selectedCatId}
                  expanded={expandedCats}
                  onSelect={selectFolder}
                  onToggleExpand={toggleExpand}
                  onEdit={setEditCatTarget}
                  onDelete={(c) => setDeleteCatTarget({ id: c.id, name: c.name })}
                  onAddSubfolder={setSubfolderParent}
                  onArchive={(id) => archiveCatMut.mutate(id)}
                  canModify={isAdmin}
                  indent={0}
                />
              ))}
            </div>
          )}

          {cats.length === 0 && (
            <div className="px-2 py-3 space-y-2">
              <p className="text-xs text-muted-foreground px-1">No folders yet.</p>
              {isSuperAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-8 gap-1.5"
                  onClick={() => seedDefaultsMut.mutate()}
                  disabled={seedDefaultsMut.isPending}
                >
                  {seedDefaultsMut.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Sparkles className="h-3.5 w-3.5" />}
                  Load Default Folders
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="bg-border mx-2 hidden md:block" />

        {/* Right: Folder overview OR document list */}
        <div ref={docsTopRef} className="min-w-0 overflow-hidden space-y-3 scroll-mt-16 md:pl-2">
          {/* Phone: where you are, with a way back (the tree is for desktop) */}
          {selectedCat && (
            <div className="flex items-center gap-1 md:hidden">
              <button
                type="button"
                aria-label="Back"
                onClick={() => selectFolder(selectedCat.parentId ?? null)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-muted-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <nav className="flex min-w-0 flex-1 flex-wrap items-center text-sm" aria-label="Folder path">
                <button type="button" className="py-1 text-muted-foreground" onClick={() => selectFolder(null)}>Folders</button>
                {folderPath.map((c, i) => (
                  <span key={c.id} className="flex items-center">
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <button type="button" className={`py-1 ${i === folderPath.length - 1 ? "font-semibold" : "text-muted-foreground"}`} onClick={() => selectFolder(c.id)}>{c.name}</button>
                  </span>
                ))}
              </nav>
            </div>
          )}
          {selectedCatId === null ? (
            /* ── Root: show folder grid ── */
            <FolderOverview
              companyCats={companyCats}
              divisionCats={divisionCats}
              childrenOf={childrenOf}
              onSelect={selectFolder}
              isAdmin={!!isAdmin}
              isSuperAdmin={!!isSuperAdmin}
              onEdit={setEditCatTarget}
              onDelete={(c) => setDeleteCatTarget({ id: c.id, name: c.name })}
              onAddSubfolder={setSubfolderParent}
              onSeedDefaults={() => seedDefaultsMut.mutate()}
              seedingDefaults={seedDefaultsMut.isPending}
            />
          ) : (
            /* ── Folder selected: subfolders + docs ── */
            <>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Status filter */}
                <div className="flex rounded-lg border text-sm overflow-hidden">
                  {(["active", "archived"] as const).map((s) => (
                    <button
                      key={s}
                      className={`px-3 py-1.5 capitalize transition-colors ${statusFilter === s ? "bg-muted font-medium" : "hover:bg-muted/50 text-muted-foreground"} ${s === "archived" ? "border-l" : ""}`}
                      onClick={() => { setStatusFilter(s); setSelectMode(false); setSelectedDocIds(new Set()); }}
                    >{s}</button>
                  ))}
                </div>
                {selectedCat && (
                  <span className="hidden text-sm text-muted-foreground items-center gap-1 mr-auto md:flex">
                    <ChevronRight className="h-3.5 w-3.5" />{selectedCat.name}
                  </span>
                )}

                {/* Folder-level assign + select mode buttons — admin only, active docs */}
                {isAdmin && statusFilter === "active" && docs.length > 0 && !selectMode && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1.5 text-blue-600 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => setBulkAssignDocs(docs.map(({ doc, category }) => ({ doc, category: category?.name ?? "" })))}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Assign Folder
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-1.5"
                      onClick={() => { setSelectMode(true); setSelectedDocIds(new Set()); }}
                    >
                      <Check className="h-3.5 w-3.5" />
                      Select
                    </Button>
                  </>
                )}

                {/* Select mode toolbar */}
                {selectMode && (
                  <>
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded border border-border"
                      onClick={() => {
                        const activeIds = docs.filter(({ doc }) => doc.status === "active" && doc.currentVersionId).map(({ doc }) => doc.id);
                        setSelectedDocIds(selectedDocIds.size === activeIds.length ? new Set() : new Set(activeIds));
                      }}
                    >
                      {selectedDocIds.size === docs.filter(({ doc }) => doc.status === "active" && doc.currentVersionId).length ? "Deselect all" : "Select all"}
                    </button>
                    <Button
                      size="sm"
                      className="h-8 text-xs gap-1.5"
                      disabled={selectedDocIds.size === 0}
                      onClick={() => {
                        const selected = docs.filter(({ doc }) => selectedDocIds.has(doc.id));
                        setBulkAssignDocs(selected.map(({ doc, category }) => ({ doc, category: category?.name ?? "" })));
                      }}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Assign {selectedDocIds.size > 0 ? `(${selectedDocIds.size})` : ""}
                    </Button>
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => { setSelectMode(false); setSelectedDocIds(new Set()); }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>

              {/* Subfolders within this folder */}
              {childrenOf(selectedCatId).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Subfolders</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {childrenOf(selectedCatId).map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => selectFolder(sub.id)}
                        className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:border-paris-red/40 hover:bg-paris-red/5 text-left transition-colors group"
                      >
                        <Folder className="h-4 w-4 text-muted-foreground group-hover:text-paris-red-text flex-shrink-0" />
                        <span className="text-xs font-medium truncate">{sub.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Document list */}
              {docsLoading ? (
                <div className="space-y-2 py-4">
                  {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
                </div>
              ) : docs.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center py-14 text-center">
                    <FileWarning className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="font-medium text-muted-foreground text-sm">No documents here yet</p>
                    {isAdmin && (
                      <p className="text-xs text-muted-foreground/70 mt-1">Click "Upload Doc" to add the first document.</p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {docs.map(({ doc, category }) => (
                    <DocRow
                      key={doc.id}
                      doc={doc}
                      categoryName={category?.name ?? "—"}
                      isSuperAdmin={!!isSuperAdmin}
                      isAdmin={!!isAdmin}
                      selectable={selectMode && doc.status === "active" && !!doc.currentVersionId}
                      selected={selectedDocIds.has(doc.id)}
                      onToggleSelect={() => setSelectedDocIds((prev) => {
                        const next = new Set(prev);
                        next.has(doc.id) ? next.delete(doc.id) : next.add(doc.id);
                        return next;
                      })}
                      onViewVersions={() => setVersionDocId(doc.id)}
                      onView={!selectMode && doc.currentVersionId ? () => fetchAndOpenDoc(doc.id, doc.currentVersionId!, "view") : undefined}
                      onDownload={!selectMode && doc.currentVersionId ? () => fetchAndOpenDoc(doc.id, doc.currentVersionId!, "download") : undefined}
                      onEdit={!selectMode && isAdmin ? () => setEditDocTarget(doc) : undefined}
                      onAssign={!selectMode && isAdmin ? () => setAssignDocTarget(doc) : undefined}
                      onQuiz={!selectMode && isAdmin ? () => setQuizDocTarget(doc) : undefined}
                      onArchive={() => archiveDocMut.mutate({ id: doc.id, action: "archive" })}
                      onRestore={() => archiveDocMut.mutate({ id: doc.id, action: "restore" })}
                      onDelete={() => setDeleteDocTarget({ id: doc.id, title: doc.title })}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* New top-level folder sheet */}
      <Sheet open={showNewCat} onOpenChange={(v) => !v && setShowNewCat(false)}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader><SheetTitle>New Folder</SheetTitle></SheetHeader>
          <NewCategoryForm
            isSuperAdmin={!!isSuperAdmin}
            divisions={divisions}
            onDone={() => { qc.invalidateQueries({ queryKey: ["admin", "safety-doc-categories"] }); setShowNewCat(false); }}
            onCancel={() => setShowNewCat(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Add subfolder sheet */}
      {subfolderParent && (
        <Sheet open onOpenChange={(v) => !v && setSubfolderParent(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md">
            <SheetHeader><SheetTitle>New Subfolder in "{subfolderParent.name}"</SheetTitle></SheetHeader>
            <NewCategoryForm
              isSuperAdmin={!!isSuperAdmin}
              divisions={divisions}
              parentId={subfolderParent.id}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ["admin", "safety-doc-categories"] });
                setExpandedCats((prev) => new Set([...prev, subfolderParent.id]));
                setSubfolderParent(null);
              }}
              onCancel={() => setSubfolderParent(null)}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* Edit folder sheet */}
      {editCatTarget && (
        <Sheet open onOpenChange={(v) => !v && setEditCatTarget(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md">
            <SheetHeader><SheetTitle>Edit Folder</SheetTitle></SheetHeader>
            <EditCategoryForm
              cat={editCatTarget}
              onDone={() => { qc.invalidateQueries({ queryKey: ["admin", "safety-doc-categories"] }); setEditCatTarget(null); }}
              onCancel={() => setEditCatTarget(null)}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* New document sheet */}
      {selectedCatId && (
        <Sheet open={showNewDoc} onOpenChange={(v) => !v && setShowNewDoc(false)}>
          <SheetContent side="right" className="w-full sm:max-w-lg">
            <SheetHeader><SheetTitle>Upload Safety Document</SheetTitle></SheetHeader>
            <NewDocumentForm
              categoryId={selectedCatId}
              isSuperAdmin={!!isSuperAdmin}
              divisions={divisions}
              onDone={() => { qc.invalidateQueries({ queryKey: ["admin", "safety-docs"] }); setShowNewDoc(false); }}
              onCancel={() => setShowNewDoc(false)}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* Version history sheet */}
      {versionDocId && (
        <Sheet open onOpenChange={(v) => !v && setVersionDocId(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg">
            <SheetHeader><SheetTitle>Version History</SheetTitle></SheetHeader>
            <VersionsPanel
              docId={versionDocId}
              isAdmin={!!isAdmin}
              onUploaded={() => {
                qc.invalidateQueries({ queryKey: ["admin", "safety-docs"] });
                qc.invalidateQueries({ queryKey: ["admin", "safety-doc-detail", versionDocId] });
              }}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* Edit document sheet */}
      {editDocTarget && (
        <Sheet open onOpenChange={(v) => !v && setEditDocTarget(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg">
            <SheetHeader><SheetTitle>Edit Document</SheetTitle></SheetHeader>
            <EditDocumentForm
              doc={editDocTarget}
              cats={cats}
              isSuperAdmin={!!isSuperAdmin}
              divisions={divisions}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ["admin", "safety-docs"] });
                setEditDocTarget(null);
              }}
              onCancel={() => setEditDocTarget(null)}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* Assign document sheet */}
      {assignDocTarget && (
        <AssignDocumentSheet
          doc={assignDocTarget}
          isSuperAdmin={!!isSuperAdmin}
          onDone={() => { setAssignDocTarget(null); toast.success("Assignment created"); }}
          onCancel={() => setAssignDocTarget(null)}
        />
      )}

      {/* Quiz management sheet */}
      {quizDocTarget && (
        <QuizManagementSheet
          doc={quizDocTarget}
          onClose={() => setQuizDocTarget(null)}
        />
      )}

      {/* Bulk / folder assignment sheet */}
      {bulkAssignDocs && (
        <BulkAssignSheet
          docs={bulkAssignDocs}
          isSuperAdmin={!!isSuperAdmin}
          onDone={() => {
            setBulkAssignDocs(null);
            setSelectMode(false);
            setSelectedDocIds(new Set());
            toast.success("Assignments created");
          }}
          onCancel={() => setBulkAssignDocs(null)}
        />
      )}

      {/* Delete folder confirm */}
      <AlertDialog open={!!deleteCatTarget} onOpenChange={(v) => !v && setDeleteCatTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete Folder</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <strong>"{deleteCatTarget?.name}"</strong>? The folder must be empty (no documents or subfolders) before it can be deleted. You'll have 10 seconds to undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteCatTarget) return;
                const { id, name } = deleteCatTarget;
                setDeleteCatTarget(null);
                deleteWithUndo(name, () => commitDeleteCat(id));
              }}
            >
              Delete Folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete document — step 1 */}
      <AlertDialog open={!!deleteDocTarget} onOpenChange={(v) => !v && setDeleteDocTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Permanently Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>"{deleteDocTarget?.title}"</strong> and all its versions. This action is logged in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteDocTarget) return;
                setDeleteDocConfirm(deleteDocTarget);
                setDeleteDocTarget(null);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete document — step 2 */}
      <AlertDialog open={!!deleteDocConfirm} onOpenChange={(v) => !v && setDeleteDocConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>"{deleteDocConfirm?.title}"</strong>. You'll have 10 seconds to undo after confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteDocConfirm) return;
                const { id, title } = deleteDocConfirm;
                setDeleteDocConfirm(null);
                deleteWithUndo(title, () => commitDeleteDoc(id));
              }}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── FolderOverview (root view) ─────────────────────────────────────────────
function FolderOverview({
  companyCats, divisionCats, childrenOf, onSelect, isAdmin, isSuperAdmin,
  onEdit, onDelete, onAddSubfolder, onSeedDefaults, seedingDefaults,
}: {
  companyCats: DocCategory[];
  divisionCats: DocCategory[];
  childrenOf: (id: string) => DocCategory[];
  onSelect: (id: string) => void;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  onEdit: (cat: DocCategory) => void;
  onDelete: (cat: DocCategory) => void;
  onAddSubfolder: (cat: DocCategory) => void;
  onSeedDefaults: () => void;
  seedingDefaults: boolean;
}) {
  const allCats = [...companyCats, ...divisionCats];

  if (allCats.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <Folder className="h-12 w-12 text-muted-foreground/20 mb-4" />
        <p className="font-medium text-muted-foreground text-sm">No folders yet</p>
        {isSuperAdmin && (
          <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={onSeedDefaults} disabled={seedingDefaults}>
            {seedingDefaults ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Load Default Folders
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {companyCats.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Company Folders</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {companyCats.map((cat) => (
              <FolderCard
                key={cat.id}
                cat={cat}
                subfolderCount={childrenOf(cat.id).length}
                onSelect={() => onSelect(cat.id)}
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
                onEdit={() => onEdit(cat)}
                onDelete={() => onDelete(cat)}
                onAddSubfolder={() => onAddSubfolder(cat)}
              />
            ))}
          </div>
        </div>
      )}
      {divisionCats.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Division Folders</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {divisionCats.map((cat) => (
              <FolderCard
                key={cat.id}
                cat={cat}
                subfolderCount={childrenOf(cat.id).length}
                onSelect={() => onSelect(cat.id)}
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
                onEdit={() => onEdit(cat)}
                onDelete={() => onDelete(cat)}
                onAddSubfolder={() => onAddSubfolder(cat)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FolderCard({
  cat, subfolderCount, onSelect, isAdmin, isSuperAdmin, onEdit, onDelete, onAddSubfolder,
}: {
  cat: DocCategory; subfolderCount: number;
  onSelect: () => void; isAdmin: boolean; isSuperAdmin: boolean;
  onEdit: () => void; onDelete: () => void; onAddSubfolder: () => void;
}) {
  return (
    <div className="group relative flex items-start gap-3 p-3 rounded-lg border border-border hover:border-paris-red/30 hover:bg-paris-red/5 transition-colors">
      <button onClick={onSelect} className="flex items-start gap-3 flex-1 min-w-0 text-left">
        <FolderOpen className="h-5 w-5 text-paris-red-text/60 flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{cat.name}</p>
          {cat.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{cat.description}</p>}
          {subfolderCount > 0 && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{subfolderCount} subfolder{subfolderCount !== 1 ? "s" : ""}</p>
          )}
        </div>
      </button>
      {isAdmin && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onAddSubfolder(); }}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Add subfolder"
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Edit folder"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {isSuperAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
              title="Delete folder"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── CatTreeRow (sidebar) ───────────────────────────────────────────────────
function CatTreeRow({
  cat, children, allCats, selected, expanded, indent,
  onSelect, onToggleExpand, onEdit, onDelete, onAddSubfolder, onArchive, canModify,
}: {
  cat: DocCategory;
  children: DocCategory[];
  allCats: DocCategory[];
  selected: string | null;
  expanded: Set<string>;
  indent: number;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onEdit: (cat: DocCategory) => void;
  onDelete: (cat: DocCategory) => void;
  onAddSubfolder: (cat: DocCategory) => void;
  onArchive: (id: string) => void;
  canModify: boolean;
}) {
  const isSelected = selected === cat.id;
  const isExpanded = expanded.has(cat.id);
  const hasChildren = children.length > 0;

  return (
    <div>
      <div
        className={`group flex items-center gap-0.5 rounded-lg transition-colors ${isSelected ? "bg-paris-red/10" : "hover:bg-muted"}`}
        style={{ paddingLeft: indent * 12 }}
      >
        {/* Expand toggle */}
        <button
          className={`p-1 rounded text-muted-foreground flex-shrink-0 ${hasChildren ? "hover:text-foreground" : "opacity-0 pointer-events-none"}`}
          onClick={() => hasChildren && onToggleExpand(cat.id)}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
          ) : <ChevronRight className="h-3 w-3" />}
        </button>

        {/* Folder button */}
        <button
          onClick={() => onSelect(cat.id)}
          className={`flex-1 flex items-center gap-2 py-1.5 text-sm text-left min-w-0 ${isSelected ? "text-paris-red-text font-medium" : "text-muted-foreground"}`}
        >
          {isSelected ? <FolderOpen className="h-4 w-4 flex-shrink-0" /> : <Folder className="h-4 w-4 flex-shrink-0" />}
          <span className="truncate">{cat.name}</span>
        </button>

        {/* Hover actions */}
        {canModify && (
          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 pr-1">
            <button
              className="p-1 rounded text-muted-foreground hover:text-foreground"
              title="Add subfolder"
              onClick={(e) => { e.stopPropagation(); onAddSubfolder(cat); }}
            >
              <FolderPlus className="h-3 w-3" />
            </button>
            <button
              className="p-1 rounded text-muted-foreground hover:text-foreground"
              title="Edit folder"
              onClick={(e) => { e.stopPropagation(); onEdit(cat); }}
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              className="p-1 rounded text-muted-foreground hover:text-destructive"
              title="Delete folder"
              onClick={(e) => { e.stopPropagation(); onDelete(cat); }}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Children */}
      {isExpanded && children.map((child) => {
        const grandchildren = allCats.filter((c) => c.parentId === child.id);
        return (
          <CatTreeRow
            key={child.id}
            cat={child}
            children={grandchildren}
            allCats={allCats}
            selected={selected}
            expanded={expanded}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddSubfolder={onAddSubfolder}
            onArchive={onArchive}
            canModify={canModify}
            indent={indent + 1}
          />
        );
      })}
    </div>
  );
}

// ── DocRow ─────────────────────────────────────────────────────────────────
function DocRow({
  doc, categoryName, isSuperAdmin, isAdmin,
  selectable, selected, onToggleSelect,
  onViewVersions, onView, onDownload, onEdit, onAssign, onQuiz, onArchive, onRestore, onDelete,
}: {
  doc: SafetyDoc; categoryName: string; isSuperAdmin: boolean; isAdmin: boolean;
  selectable?: boolean; selected?: boolean; onToggleSelect?: () => void;
  onViewVersions: () => void;
  onView?: () => void;
  onDownload?: () => void;
  onEdit?: () => void;
  onAssign?: () => void;
  onQuiz?: () => void;
  onArchive: () => void; onRestore: () => void; onDelete: () => void;
}) {
  const isArchived = doc.status === "archived";
  return (
    <Card className={`p-0 ${isArchived ? "opacity-60" : ""} ${selected ? "ring-2 ring-blue-500 ring-inset" : ""}`}>
      <CardContent className="flex flex-wrap items-center gap-2 px-3 py-2 md:flex-nowrap">
        {selectable ? (
          <button
            type="button"
            onClick={onToggleSelect}
            className={`h-5 w-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
              selected ? "bg-blue-600 border-blue-600" : "border-muted-foreground hover:border-blue-500"
            }`}
          >
            {selected && <Check className="h-3 w-3 text-white" />}
          </button>
        ) : (
          <FileText className="h-6 w-6 text-paris-red-text/50 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-medium text-sm break-words md:text-xs md:truncate">{doc.title}</p>
            <VisibilityBadge visibility={doc.visibility} />
            {doc.hasQuiz && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 font-medium">Quiz</span>
            )}
            {isArchived && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">Archived</span>
            )}
          </div>
          {doc.description && (
            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{doc.description}</p>
          )}
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {categoryName} · Updated {new Date(doc.updatedAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center justify-end gap-0.5 md:w-auto md:flex-shrink-0 md:flex-nowrap">
          {onView && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View" onClick={onView}>
              <Eye className="h-3.5 w-3.5" />
            </Button>
          )}
          {onDownload && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download" onClick={onDownload}>
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
          {onEdit && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Edit info" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {onAssign && !isArchived && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700" title="Assign to crew" onClick={onAssign}>
              <UserPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          {onQuiz && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-violet-600 hover:text-violet-700" title="Manage quiz" onClick={onQuiz}>
              <ClipboardList className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Version history" onClick={onViewVersions}>
            <History className="h-3.5 w-3.5" />
          </Button>
          {isAdmin && (
            isArchived ? (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600" title="Restore" onClick={onRestore}>
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Archive" onClick={onArchive}>
                <Archive className="h-3.5 w-3.5" />
              </Button>
            )
          )}
          {isSuperAdmin && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" title="Permanently delete" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── BulkAssignSheet ────────────────────────────────────────────────────────
function BulkAssignSheet({
  docs,
  isSuperAdmin,
  onDone,
  onCancel,
}: {
  docs: { doc: SafetyDoc; category: string }[];
  isSuperAdmin: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [targets, setTargets] = useState<AssignTarget[]>([]);
  const [allTeam, setAllTeam] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [message, setMessage] = useState("");
  const [quizChoice, setQuizChoice] = useState<QuizChoice>(DEFAULT_QUIZ_CHOICE);
  const [saving, setSaving] = useState(false);

  const { data: divsData } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/admin/divisions"),
  });
  const divisions = divsData?.data ?? [];

  const { data: jobTitlesData } = useQuery({
    queryKey: ["admin", "job-titles"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/admin/job-titles"),
  });
  const jobTitles = jobTitlesData?.data ?? [];

  const { data: usersData } = useQuery({
    queryKey: ["admin", "users-list"],
    queryFn: () => apiGet<{ data: { id: string; name: string; email: string; role: string | null; divisionId: string | null; divisionIds: string[]; jobTitleId: string | null; supervisedDivisionIds: string[] }[] }>("/api/users"),
  });
  const usersList = usersData?.data ?? [];

  const selectedRoles = targets.filter((t) => t.type === "role").map((t) => t.value!);
  const selectedJobTitles = targets.filter((t) => t.type === "job_title").map((t) => t.value!);
  const selectedDivisions = targets.filter((t) => t.type === "division").map((t) => t.value!);
  const selectedUsers = targets.filter((t) => t.type === "user").map((t) => t.value!);

  function handleAllTeamToggle() {
    if (allTeam) { setAllTeam(false); setTargets([]); }
    else { setAllTeam(true); setTargets([{ type: "all", label: "All Team" }]); }
  }

  function handleTypeToggle(type: Exclude<TargetType, "all">, value: string, label: string) {
    if (allTeam) setAllTeam(false);
    setTargets((prev) => {
      const withoutAll = prev.filter((p) => p.type !== "all");
      const key = `${type}:${value}`;
      const exists = withoutAll.some((p) => `${p.type}:${p.value}` === key);
      return exists ? withoutAll.filter((p) => `${p.type}:${p.value}` !== key) : [...withoutAll, { type, value, label }];
    });
  }

  const totalTargetCount = allTeam ? 1 : selectedRoles.length + selectedJobTitles.length + selectedDivisions.length + selectedUsers.length;

  const assignableDocs = docs.filter(({ doc }) => doc.status === "active" && doc.currentVersionId);

  async function handleSubmit() {
    if (totalTargetCount === 0) { toast.error("Select at least one target"); return; }
    if (assignableDocs.length === 0) { toast.error("No assignable documents selected"); return; }
    setSaving(true);
    try {
      const finalTargets = allTeam
        ? [{ type: "all" as const }]
        : targets.map((t) => ({ type: t.type, ...(t.value != null ? { value: t.value } : {}) }));
      await apiPost("/api/admin/doc-assignments/bulk", {
        documents: assignableDocs.map(({ doc }) => ({ documentId: doc.id, versionId: doc.currentVersionId! })),
        targets: finalTargets,
        dueDate: dueDate || null,
        message: message || null,
        ...(({ questionCount, passScore, enableQuiz }) => ({ questionCount, passScore, enableQuiz }))(normalizeQuizChoice(quizChoice)),
      });
      onDone();
    } catch (e: any) {
      toast.error(e.message || "Failed to create assignments");
    } finally {
      setSaving(false);
    }
  }

  const roleOptions = Object.entries(ROLE_LABELS).map(([v, label]) => ({ value: v, label }));
  const jobTitleOptions = jobTitles.map((jt) => ({ value: jt.id, label: jt.name }));
  const divisionOptions = divisions.map((d) => ({ value: d.id, label: d.name }));
  const filteredUsers = usersList.filter((u) => {
    if (selectedUsers.includes(u.id)) return true;
    const hasGroupFilter = selectedRoles.length > 0 || selectedJobTitles.length > 0 || selectedDivisions.length > 0;
    if (!hasGroupFilter) return true;
    if (selectedRoles.length > 0 && !selectedRoles.includes(u.role ?? "")) return false;
    if (selectedJobTitles.length > 0 && !selectedJobTitles.includes(u.jobTitleId ?? "")) return false;
    if (selectedDivisions.length > 0) {
      const allUserDivs = [...(u.divisionIds ?? []), ...(u.supervisedDivisionIds ?? [])];
      if (!allUserDivs.some((d) => selectedDivisions.includes(d))) return false;
    }
    return true;
  });
  const userOptions = filteredUsers.map((u) => ({ value: u.id, label: u.name || u.email }));

  return (
    <Sheet open onOpenChange={(v) => !v && onCancel()}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            Assign {assignableDocs.length} Document{assignableDocs.length !== 1 ? "s" : ""}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-3 pr-1">
          {/* Doc list preview */}
          <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border max-h-44 overflow-y-auto">
            {assignableDocs.map(({ doc, category }) => (
              <div key={doc.id} className="flex items-center gap-2 px-3 py-2">
                <FileText className="h-4 w-4 text-paris-red-text/50 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{doc.title}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{category}</p>
                </div>
              </div>
            ))}
            {docs.length > assignableDocs.length && (
              <p className="px-3 py-2 text-[11px] text-amber-600">
                {docs.length - assignableDocs.length} document{docs.length - assignableDocs.length !== 1 ? "s" : ""} skipped (archived or no version)
              </p>
            )}
          </div>

          {/* Assign To */}
          <div className="space-y-4">
            <Label className="text-xs font-semibold uppercase tracking-wide">Assign To</Label>

            <button
              type="button"
              onClick={handleAllTeamToggle}
              className={`w-full flex items-center gap-3 h-10 px-3 rounded-md border text-sm transition-colors ${
                allTeam
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium"
                  : "border-border hover:border-foreground/40 text-muted-foreground"
              }`}
            >
              <span className={`h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${allTeam ? "bg-blue-600 border-blue-600" : "border-border"}`}>
                {allTeam && <Check className="h-2.5 w-2.5 text-white" />}
              </span>
              <span>Entire Company (All Team Members)</span>
            </button>

            <div className={`space-y-4 ${allTeam ? "opacity-40 pointer-events-none" : ""}`}>
              <MultiSelectDropdown label="By Role" placeholder="Select roles…" options={roleOptions} selected={selectedRoles} onToggle={(v, lbl) => handleTypeToggle("role", v, lbl)} />
              {jobTitles.length > 0 && <MultiSelectDropdown label="By Job Title" placeholder="Select job titles…" options={jobTitleOptions} selected={selectedJobTitles} onToggle={(v, lbl) => handleTypeToggle("job_title", v, lbl)} searchable={jobTitles.length > 8} />}
              {divisions.length > 0 && <MultiSelectDropdown label="By Division" placeholder="Select divisions…" options={divisionOptions} selected={selectedDivisions} onToggle={(v, lbl) => handleTypeToggle("division", v, lbl)} />}
              {usersList.length > 0 && <MultiSelectDropdown label="Individual Users" placeholder="Search and select users…" options={userOptions} selected={selectedUsers} onToggle={(v, lbl) => handleTypeToggle("user", v, lbl)} searchable />}
            </div>

            {totalTargetCount > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2">
                <Users className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                  {allTeam ? "Assigning to entire company" : `${totalTargetCount} group${totalTargetCount !== 1 ? "s" : ""} selected`}
                </p>
              </div>
            )}
          </div>

          <QuizAssignmentSettings
            docs={assignableDocs.map(({ doc }) => ({ id: doc.id, title: doc.title }))}
            value={quizChoice}
            onChange={setQuizChoice}
          />

          {/* Due date */}
          <div className="space-y-1.5">
            <Label htmlFor="bulk-assign-due" className="text-xs font-semibold uppercase tracking-wide">
              Due Date <span className="text-muted-foreground font-normal normal-case">(optional)</span>
            </Label>
            <div className="relative">
              <CalendarIcon className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input id="bulk-assign-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="pl-8 text-sm" />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <Label htmlFor="bulk-assign-msg" className="text-xs font-semibold uppercase tracking-wide">
              Message <span className="text-muted-foreground font-normal normal-case">(optional)</span>
            </Label>
            <Textarea id="bulk-assign-msg" placeholder="Add a note for the assignees…" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="text-sm resize-none" />
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button className="flex-1" onClick={handleSubmit} disabled={saving || totalTargetCount === 0}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Assign {assignableDocs.length > 0 ? assignableDocs.length : ""} Doc{assignableDocs.length !== 1 ? "s" : ""}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── QuizManagementSheet ────────────────────────────────────────────────────
interface QuizQuestion {
  id?: string;
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctOption: string;
}

function emptyQuestion(): QuizQuestion {
  return { questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctOption: "a" };
}

function QuizManagementSheet({ doc, onClose }: { doc: SafetyDoc; onClose: () => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"settings" | "questions" | "import">("settings");
  const [passScore, setPassScore] = useState(65);
  const [questions, setQuestions] = useState<QuizQuestion[]>([emptyQuestion()]);
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [xlsxDragging, setXlsxDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const xlsxInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "doc-quiz", doc.id],
    queryFn: () => apiGet<{ quiz: { id: string; passScore: number; isActive?: boolean } | null; questions: QuizQuestion[] }>(`/api/admin/safety-docs/${doc.id}/quiz`),
  });

  // The quiz can be switched off while its question bank is kept.
  const quizEnabled = !!data?.quiz && data.quiz.isActive !== false;
  const hasBank = !!data?.quiz;

  useEffect(() => {
    if (data?.quiz) setPassScore(data.quiz.passScore);
    if (data?.questions?.length) setQuestions(data.questions.map(q => ({ ...q })));
  }, [data]);

  async function handleToggleQuiz(enable: boolean) {
    setSaving(true);
    try {
      await apiPut(`/api/admin/safety-docs/${doc.id}/quiz`, { enabled: enable, passScore });
      qc.invalidateQueries({ queryKey: ["admin", "doc-quiz", doc.id] });
      toast.success(enable ? "Quiz enabled" : "Quiz disabled");
    } catch (e: any) { toast.error(e.message || "Failed"); }
    finally { setSaving(false); }
  }

  async function handleSaveSettings() {
    setSaving(true);
    try {
      await apiPut(`/api/admin/safety-docs/${doc.id}/quiz`, { enabled: quizEnabled, passScore });
      qc.invalidateQueries({ queryKey: ["admin", "doc-quiz", doc.id] });
      toast.success("Quiz settings saved");
    } catch (e: any) { toast.error(e.message || "Failed"); }
    finally { setSaving(false); }
  }

  async function handleSaveQuestions() {
    const valid = questions.filter(q => q.questionText.trim() && q.optionA.trim() && q.optionB.trim());
    if (valid.length === 0) { toast.error("Add at least one question with question text, option A, and option B"); return; }
    setSaving(true);
    try {
      await apiPost(`/api/admin/safety-docs/${doc.id}/quiz/questions`, { questions: valid });
      qc.invalidateQueries({ queryKey: ["admin", "doc-quiz", doc.id] });
      toast.success(`${valid.length} questions saved`);
    } catch (e: any) { toast.error(e.message || "Failed"); }
    finally { setSaving(false); }
  }

  function addQuestion() { setQuestions(prev => [...prev, emptyQuestion()]); }
  function removeQuestion(i: number) { setQuestions(prev => prev.filter((_, idx) => idx !== i)); }
  function updateQuestion(i: number, field: keyof QuizQuestion, val: string) {
    setQuestions(prev => prev.map((q, idx) => idx === i ? { ...q, [field]: val } : q));
  }

  async function handleImport() {
    if (!xlsxFile) { toast.error("Select an Excel file first"); return; }
    setImporting(true);
    try {
      const buf = await xlsxFile.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: "" });

      const parsed: { questionText: string; optionA: string; optionB: string; optionC: string; optionD: string; correctOption: string }[] = [];
      const skipped: number[] = [];

      rows.forEach((row, idx) => {
        const q = String(row["Question"] ?? row["question_text"] ?? "").trim();
        const a = String(row["Option A"] ?? row["option_a"] ?? "").trim();
        const b = String(row["Option B"] ?? row["option_b"] ?? "").trim();
        const c = String(row["Option C"] ?? row["option_c"] ?? "").trim();
        const d = String(row["Option D"] ?? row["option_d"] ?? "").trim();
        const ans = String(row["Correct (A/B/C/D)"] ?? row["Correct Answer (A/B/C/D)"] ?? row["correct_option"] ?? "").trim().toLowerCase();
        const correctOption = ans === "1" ? "a" : ans === "2" ? "b" : ans === "3" ? "c" : ans === "4" ? "d" : ans;

        if (!q || !a || !b || !["a","b","c","d"].includes(correctOption)) { skipped.push(idx + 2); return; }
        parsed.push({ questionText: q, optionA: a, optionB: b, optionC: c, optionD: d, correctOption });
      });

      if (parsed.length === 0) throw new Error("No valid questions found. Check column headers and correct answer values.");
      await apiPost(`/api/admin/safety-docs/${doc.id}/quiz/questions`, { questions: parsed });
      qc.invalidateQueries({ queryKey: ["admin", "doc-quiz", doc.id] });
      toast.success(`${parsed.length} questions imported${skipped.length ? ` · ${skipped.length} rows skipped` : ""}`);
      setXlsxFile(null);
    } catch (e: any) { toast.error(e.message || "Import failed"); }
    finally { setImporting(false); }
  }

  function handleDownloadTemplate() {
    const existing = (data?.questions ?? []) as QuizQuestion[];
    const BLUE = "1F4E79"; const LIGHT = "D9E1F2";
    const headerStyle = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: BLUE } }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, alignment: { horizontal: "center" } };
    const cellStyle = (light: boolean) => ({ fill: { fgColor: { rgb: light ? LIGHT : "FFFFFF" } }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, alignment: { wrapText: true, vertical: "top" } });
    const centreStyle = (light: boolean) => ({ ...cellStyle(light), alignment: { horizontal: "center", vertical: "top" } });

    const headers = ["#", "Question", "Option A", "Option B", "Option C", "Option D", "Correct (A/B/C/D)"];
    const wsData: XLSX.CellObject[][] = [
      headers.map(h => ({ v: h, t: "s", s: headerStyle } as XLSX.CellObject)),
    ];

    const blankRows = 20;
    const totalRows = Math.max(existing.length, 0) + blankRows;
    for (let i = 0; i < totalRows; i++) {
      const q = existing[i];
      const light = i % 2 === 0;
      const num: XLSX.CellObject = { v: i + 1, t: "n", s: centreStyle(light) };
      const ans: XLSX.CellObject = { v: q ? (q.correctOption ?? "").toUpperCase() : "", t: "s", s: centreStyle(light) };
      wsData.push([
        num,
        { v: q?.questionText ?? "", t: "s", s: cellStyle(light) },
        { v: q?.optionA ?? "", t: "s", s: cellStyle(light) },
        { v: q?.optionB ?? "", t: "s", s: cellStyle(light) },
        { v: q?.optionC ?? "", t: "s", s: cellStyle(light) },
        { v: q?.optionD ?? "", t: "s", s: cellStyle(light) },
        ans,
      ]);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 5 }, { wch: 55 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 22 }];
    ws["!rows"] = [{ hpt: 20 }];
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Questions");
    const safeTitle = doc.title.replace(/[^a-z0-9]/gi, "_").slice(0, 40);
    XLSX.writeFile(wb, `quiz_${safeTitle}.xlsx`);
  }

  async function apiPut(url: string, body: unknown) {
    const BASE = import.meta.env.VITE_API_URL ?? "";
    const res = await fetch(`${BASE}${url}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) { const j = await res.json() as { error?: string }; throw new Error(j.error ?? "Request failed"); }
    return res.json();
  }

  const OPTION_LABELS = ["A", "B", "C", "D"] as const;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-violet-600" />
            Quiz Management
          </SheetTitle>
          <p className="text-sm text-muted-foreground truncate">{doc.title}</p>
        </SheetHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Quiz toggle */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="text-sm font-medium">Quiz Required</p>
                <p className="text-xs text-muted-foreground">
                  {quizEnabled
                    ? "On: every assignment of this document needs the quiz. Assigners can't turn it off."
                    : hasBank
                      ? "Off: question bank kept. Assigners can still choose to give the quiz on a single assignment."
                      : "Assignees must pass quiz before signing"}
                </p>
              </div>
              <button
                onClick={() => handleToggleQuiz(!quizEnabled)}
                disabled={saving}
                className={`relative h-6 w-11 rounded-full transition-colors ${quizEnabled ? "bg-violet-600" : "bg-muted-foreground/30"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${quizEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>

            {hasBank ? (
              <>
                {/* Tab bar */}
                <div className="flex border-b text-sm">
                  {(["settings", "questions", "import"] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      className={`px-4 py-2.5 capitalize border-b-2 transition-colors ${
                        tab === t ? "border-violet-600 text-violet-700 dark:text-violet-300 font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >{t === "import" ? "Excel Import" : t.charAt(0).toUpperCase() + t.slice(1)}</button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto py-4 space-y-4">
                  {tab === "settings" && (
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label className="text-sm">Pass Score (%)</Label>
                        <p className="text-xs text-muted-foreground">Minimum percentage required to pass (65% or higher)</p>
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            min={65}
                            max={100}
                            value={passScore}
                            onChange={(e) => setPassScore(parseInt(e.target.value) || 65)}
                            onBlur={() => setPassScore((v) => Math.min(100, Math.max(65, v || 65)))}
                            className="w-24 h-9"
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                      <div className="rounded-lg border p-3 bg-muted/30">
                        <p className="text-xs text-muted-foreground">
                          <strong>{data?.questions?.length ?? 0}</strong> questions in the bank.
                          When assigning, choose how many questions (5, 10, or 15) to randomly serve each assignee.
                        </p>
                      </div>
                      <Button size="sm" onClick={handleSaveSettings} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                        Save Settings
                      </Button>
                    </div>
                  )}

                  {tab === "questions" && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">{questions.length} question{questions.length !== 1 ? "s" : ""} in bank</p>
                        <Button variant="outline" size="sm" onClick={addQuestion} className="h-7 text-xs">
                          <Plus className="h-3.5 w-3.5 mr-1" />Add
                        </Button>
                      </div>

                      {questions.map((q, i) => (
                        <div key={i} className="border rounded-lg p-3 space-y-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground w-5 flex-shrink-0">Q{i + 1}</span>
                            <Input
                              value={q.questionText}
                              onChange={(e) => updateQuestion(i, "questionText", e.target.value)}
                              placeholder="Question text"
                              className="h-8 text-sm flex-1"
                            />
                            <button
                              onClick={() => removeQuestion(i)}
                              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive flex-shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 pl-7">
                            {(["optionA", "optionB", "optionC", "optionD"] as const).map((opt, oi) => {
                              const optKey = ["a", "b", "c", "d"][oi];
                              const isCorrect = q.correctOption === optKey;
                              return (
                                <div key={opt} className={`flex items-center gap-1.5 rounded border px-2 py-1 ${isCorrect ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20" : "border-border"}`}>
                                  <button
                                    type="button"
                                    onClick={() => updateQuestion(i, "correctOption", optKey)}
                                    className={`h-4 w-4 rounded-full border flex-shrink-0 flex items-center justify-center ${isCorrect ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground"}`}
                                    title={`Mark option ${OPTION_LABELS[oi]} as correct`}
                                  >
                                    {isCorrect && <Check className="h-2.5 w-2.5 text-white" />}
                                  </button>
                                  <span className="text-[10px] font-bold text-muted-foreground flex-shrink-0">{OPTION_LABELS[oi]}</span>
                                  <Input
                                    value={q[opt] ?? ""}
                                    onChange={(e) => updateQuestion(i, opt, e.target.value)}
                                    placeholder={`Option ${OPTION_LABELS[oi]}${oi < 2 ? " *" : " (opt.)"}`}
                                    className="h-6 text-xs border-0 bg-transparent px-0 focus-visible:ring-0 flex-1"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      <Button onClick={handleSaveQuestions} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                        Save All Questions
                      </Button>
                    </div>
                  )}

                  {tab === "import" && (
                    <div className="space-y-4">
                      <div className="rounded-lg border p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">Excel import replaces all questions</p>
                            <p className="text-xs text-muted-foreground">
                              Download the template, fill in your questions, then upload. Columns: <span className="font-medium">Question, Option A–D, Correct (A/B/C/D)</span>. Options C and D are optional.
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-1.5">
                          <Download className="h-3.5 w-3.5" />
                          Download Excel Template
                        </Button>
                      </div>

                      <div
                        onClick={() => xlsxInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setXlsxDragging(true); }}
                        onDragLeave={() => setXlsxDragging(false)}
                        onDrop={(e) => { e.preventDefault(); setXlsxDragging(false); const f = e.dataTransfer.files[0]; if (f) setXlsxFile(f); }}
                        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 cursor-pointer transition-colors ${
                          xlsxDragging ? "border-violet-400 bg-violet-50/30 dark:bg-violet-900/20" : xlsxFile ? "border-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/20" : "border-border hover:border-violet-400 hover:bg-violet-50/30 dark:hover:bg-violet-900/20"
                        }`}
                      >
                        <input ref={xlsxInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setXlsxFile(f); }} />
                        {xlsxFile ? (
                          <>
                            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{xlsxFile.name}</p>
                            <p className="text-xs text-muted-foreground">Click to change file</p>
                          </>
                        ) : (
                          <>
                            <Upload className="h-6 w-6 text-muted-foreground/50" />
                            <p className="text-sm text-muted-foreground">Click to browse or drag & drop an Excel file</p>
                          </>
                        )}
                      </div>

                      <Button onClick={handleImport} disabled={!xlsxFile || importing} className="w-full bg-violet-600 hover:bg-violet-700">
                        {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                        Import Questions
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
                <ClipboardList className="h-12 w-12 text-muted-foreground/20" />
                <div>
                  <p className="font-medium text-muted-foreground text-sm">No quiz configured</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">Toggle quiz on above to add questions and require assignees to pass.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── FileDropZone ───────────────────────────────────────────────────────────
function FileDropZone({ file, onFile, accept = ".pdf,.doc,.docx,.xls,.xlsx" }: {
  file: File | null;
  onFile: (f: File) => void;
  accept?: string;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFile(dropped);
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`mt-1 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 cursor-pointer transition-colors ${
        dragging
          ? "border-paris-red bg-paris-red/5"
          : file
          ? "border-emerald-400 bg-emerald-500/5"
          : "border-border hover:border-paris-red/50 hover:bg-muted/40"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
      />
      {file ? (
        <>
          <FileText className="h-6 w-6 text-emerald-600" />
          <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 text-center break-all">{file.name}</p>
          <p className="text-[11px] text-muted-foreground">{formatSize(file.size)} — click to change</p>
        </>
      ) : (
        <>
          <Upload className="h-6 w-6 text-muted-foreground/50" />
          <p className="text-xs text-muted-foreground text-center">
            <span className="font-medium text-foreground">Click to browse</span> or drag & drop
          </p>
          <p className="text-[11px] text-muted-foreground">PDF, DOCX, XLSX — max 50 MB</p>
        </>
      )}
    </div>
  );
}

// ── NewCategoryForm ────────────────────────────────────────────────────────
function NewCategoryForm({
  isSuperAdmin, divisions, parentId, onDone, onCancel,
}: {
  isSuperAdmin: boolean; divisions: Division[];
  parentId?: string;
  onDone: () => void; onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [scope, setScope] = useState<"company" | "division">(isSuperAdmin ? "company" : "division");
  const [divisionId, setDivisionId] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await apiPost("/api/admin/safety-docs/categories", {
        name: name.trim(),
        description: desc.trim() || undefined,
        scope,
        divisionId: scope === "division" ? (divisionId || undefined) : undefined,
        parentId: parentId ?? undefined,
      });
      toast.success("Folder created");
      onDone();
    } catch (e: any) {
      toast.error(e.message || "Failed to create folder");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 pt-4">
      <div>
        <Label className="text-sm">Name *</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Fall Protection" className="h-9 mt-1" />
      </div>
      <div>
        <Label className="text-sm">Description</Label>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" className="h-9 mt-1" />
      </div>
      {isSuperAdmin && !parentId && (
        <div>
          <Label className="text-sm">Scope</Label>
          <Select value={scope} onValueChange={(v) => setScope(v as any)}>
            <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="company">Company-wide</SelectItem>
              <SelectItem value="division">Division-specific</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {scope === "division" && isSuperAdmin && !parentId && divisions.length > 0 && (
        <div>
          <Label className="text-sm">Division</Label>
          <SearchableSelect
            className="mt-1"
            value={divisionId}
            onChange={setDivisionId}
            placeholder="Select division"
            options={divisions.map((d) => ({ value: d.id, label: d.name }))}
          />
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="flex-1 bg-paris-red hover:bg-paris-red/90" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Folder"}
        </Button>
      </div>
    </div>
  );
}

// ── EditCategoryForm ───────────────────────────────────────────────────────
function EditCategoryForm({
  cat, onDone, onCancel,
}: {
  cat: DocCategory; onDone: () => void; onCancel: () => void;
}) {
  const [name, setName] = useState(cat.name);
  const [desc, setDesc] = useState(cat.description ?? "");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await apiPatch(`/api/admin/safety-docs/categories/${cat.id}`, { name: name.trim(), description: desc.trim() || undefined });
      toast.success("Folder updated");
      onDone();
    } catch (e: any) {
      toast.error(e.message || "Failed to update folder");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 pt-4">
      <div>
        <Label className="text-sm">Name *</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 mt-1" />
      </div>
      <div>
        <Label className="text-sm">Description</Label>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" className="h-9 mt-1" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="flex-1 bg-paris-red hover:bg-paris-red/90" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// ── NewDocumentForm ────────────────────────────────────────────────────────
function NewDocumentForm({
  categoryId, isSuperAdmin, divisions, onDone, onCancel,
}: {
  categoryId: string; isSuperAdmin: boolean; divisions: Division[];
  onDone: () => void; onCancel: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [visibility, setVisibility] = useState<"everyone" | "divisions" | "users" | "none">("everyone");
  const [visTargets, setVisTargets] = useState<string[]>([]);
  const [changeNote, setChangeNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    if (!title.trim()) return toast.error("Title is required");
    if (!file) return toast.error("Please select a file");
    if (!changeNote.trim()) return toast.error("Version note is required");
    setSaving(true);
    try {
      const doc = await apiPost<SafetyDoc>("/api/admin/safety-docs", {
        title: title.trim(),
        description: desc.trim() || undefined,
        categoryId,
        visibility,
        visibilityTargets: visTargets.map((id) => ({ type: "division", id })),
      });

      const fd = new FormData();
      fd.append("file", file);
      if (changeNote.trim()) fd.append("changeNote", changeNote.trim());
      const BASE = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${BASE}/api/admin/safety-docs/${doc.id}/versions`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({})) as any;
        throw new Error(b.error || "File upload failed");
      }

      toast.success("Document uploaded");
      onDone();
    } catch (e: any) {
      toast.error(e.message || "Failed to upload document");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 pt-4">
      <div>
        <Label className="text-sm">Title *</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Fall Protection SWP" className="h-9 mt-1" />
      </div>
      <div>
        <Label className="text-sm">Description</Label>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Optional summary" className="mt-1 resize-none text-sm" />
      </div>
      <div>
        <Label className="text-sm">Visibility</Label>
        <Select value={visibility} onValueChange={(v) => setVisibility(v as any)}>
          <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="everyone">Everyone</SelectItem>
            <SelectItem value="divisions">Specific divisions</SelectItem>
            <SelectItem value="users">Specific users</SelectItem>
            <SelectItem value="none">Admins only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {visibility === "divisions" && isSuperAdmin && divisions.length > 0 && (
        <div>
          <Label className="text-sm">Select divisions</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {divisions.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setVisTargets((ids) => ids.includes(d.id) ? ids.filter((x) => x !== d.id) : [...ids, d.id])}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  visTargets.includes(d.id)
                    ? "bg-paris-red/10 border-paris-red-text text-paris-red-text"
                    : "border-border text-muted-foreground hover:border-foreground"
                }`}
              >{d.name}</button>
            ))}
          </div>
        </div>
      )}
      <div>
        <Label className="text-sm">File * (PDF, DOCX, XLSX — max 50MB)</Label>
        <FileDropZone file={file} onFile={setFile} />
      </div>
      <div>
        <Label className="text-sm">Version note <span className="text-red-400">*</span></Label>
        <Input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder="e.g., Initial release" className="h-9 mt-1" />
      </div>
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button
          size="sm"
          className="flex-1 bg-paris-red hover:bg-paris-red/90"
          onClick={handleCreate}
          disabled={saving || !file || !title.trim() || !changeNote.trim()}
        >
          {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading…</> : "Upload Document"}
        </Button>
      </div>
    </div>
  );
}

// ── EditDocumentForm ───────────────────────────────────────────────────────
function EditDocumentForm({
  doc, cats, isSuperAdmin, divisions, onDone, onCancel,
}: {
  doc: SafetyDoc; cats: DocCategory[]; isSuperAdmin: boolean; divisions: Division[];
  onDone: () => void; onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(doc.title);
  const [desc, setDesc] = useState(doc.description ?? "");
  const [categoryId, setCategoryId] = useState(doc.categoryId);
  const [visibility, setVisibility] = useState(doc.visibility);
  const [visTargets, setVisTargets] = useState<string[]>([]);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch current version to get existing file name
  const { data: detail } = useQuery({
    queryKey: ["admin", "safety-doc-detail", doc.id],
    queryFn: () => apiGet<DocDetail>(`/api/admin/safety-docs/${doc.id}`),
  });
  const currentVersion = detail?.versions.find((v) => v.version.status === "current");
  const currentFileName = currentVersion?.file?.fileName ?? "";
  const currentVersionId = currentVersion?.version.id;

  // Sync fileName whenever the cached detail updates (handles post-save refresh)
  useEffect(() => {
    if (currentFileName) setFileName(currentFileName);
  }, [currentFileName]);

  async function handleSave() {
    if (!title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await apiPatch(`/api/admin/safety-docs/${doc.id}`, {
        title: title.trim(),
        description: desc.trim() || undefined,
        categoryId,
        visibility,
        visibilityTargets: (visibility === "divisions" || visibility === "users")
          ? visTargets.map((id) => ({ type: "division", id }))
          : [],
      });
      // Rename file if changed
      const effectiveName = fileName.trim() || currentFileName;
      if (currentVersionId && effectiveName && effectiveName !== currentFileName) {
        await apiPatch(`/api/admin/safety-docs/${doc.id}/versions/${currentVersionId}/rename`, { fileName: effectiveName });
      }
      await qc.invalidateQueries({ queryKey: ["admin", "safety-doc-detail", doc.id] });
      toast.success("Document updated");
      onDone();
    } catch (e: any) {
      toast.error(e.message || "Failed to update document");
    } finally {
      setSaving(false);
    }
  }

  const topLevelCats = cats.filter((c) => !c.parentId);

  return (
    <div className="space-y-4 pt-4">
      <div>
        <Label className="text-sm">Title *</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 mt-1" />
      </div>
      <div>
        <Label className="text-sm">Description</Label>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Optional summary" className="mt-1 resize-none text-sm" />
      </div>
      <div>
        <Label className="text-sm">Folder</Label>
        <SearchableSelect
          className="mt-1"
          value={categoryId}
          onChange={setCategoryId}
          placeholder="Select folder"
          options={topLevelCats.map((c) => ({ value: c.id, label: c.name }))}
        />
      </div>
      <div>
        <Label className="text-sm">Visibility</Label>
        <Select value={visibility} onValueChange={(v) => setVisibility(v as any)}>
          <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="everyone">Everyone</SelectItem>
            <SelectItem value="divisions">Specific divisions</SelectItem>
            <SelectItem value="users">Specific users</SelectItem>
            <SelectItem value="none">Admins only</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {visibility === "divisions" && isSuperAdmin && divisions.length > 0 && (
        <div>
          <Label className="text-sm">Select divisions</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {divisions.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setVisTargets((ids) => ids.includes(d.id) ? ids.filter((x) => x !== d.id) : [...ids, d.id])}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  visTargets.includes(d.id)
                    ? "bg-paris-red/10 border-paris-red-text text-paris-red-text"
                    : "border-border text-muted-foreground hover:border-foreground"
                }`}
              >{d.name}</button>
            ))}
          </div>
        </div>
      )}
      {currentFileName && (
        <div>
          <Label className="text-sm">File Name (current version)</Label>
          <Input
            value={fileName || currentFileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder={currentFileName}
            className="h-9 mt-1 font-mono text-xs"
          />
          <p className="text-[11px] text-muted-foreground mt-1">This is the name used when the file is downloaded.</p>
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="flex-1 bg-paris-red hover:bg-paris-red/90" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// ── VersionsPanel ──────────────────────────────────────────────────────────
function VersionsPanel({
  docId, isAdmin, onUploaded,
}: {
  docId: string; isAdmin: boolean; onUploaded: () => void;
}) {
  const qc = useQueryClient();
  const [changeNote, setChangeNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [renamingVersionId, setRenamingVersionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "safety-doc-detail", docId],
    queryFn: () => apiGet<DocDetail>(`/api/admin/safety-docs/${docId}`),
  });

  async function handleVersionUpload() {
    if (!file) return;
    if (!changeNote.trim()) return toast.error("Version note is required");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("changeNote", changeNote.trim());
      const BASE = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${BASE}/api/admin/safety-docs/${docId}/versions`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({})) as any;
        throw new Error(b.error || "Upload failed");
      }
      toast.success("Version updated — previous version archived");
      setFile(null); setChangeNote(""); setShowUpload(false);
      await qc.invalidateQueries({ queryKey: ["admin", "safety-doc-detail", docId] });
      onUploaded();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleRename(versionId: string) {
    if (!renameValue.trim()) return;
    try {
      await apiPatch(`/api/admin/safety-docs/${docId}/versions/${versionId}/rename`, { fileName: renameValue.trim() });
      await qc.invalidateQueries({ queryKey: ["admin", "safety-doc-detail", docId] });
      toast.success("File renamed");
      setRenamingVersionId(null);
      onUploaded();
    } catch (e: any) {
      toast.error(e.message || "Failed to rename");
    }
  }

  async function fetchAndOpen(versionId: string, mode: "view" | "download") {
    try {
      await openDocFile(docId, versionId, mode);
    } catch (e: any) {
      toast.error(e.message || "Failed to open file");
    }
  }

  const versions = data?.versions ?? [];

  return (
    <div className="space-y-4 pt-4">
      {data?.doc && (
        <div>
          <p className="font-medium text-sm">{data.doc.title}</p>
          <div className="flex items-center gap-2 mt-1">
            <VisibilityBadge visibility={data.doc.visibility} />
            <span className="text-xs text-muted-foreground">{versions.length} version{versions.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
      )}

      {isAdmin && (
        <div>
          {showUpload ? (
            <div className="border rounded-lg p-3 space-y-2">
              <FileDropZone file={file} onFile={setFile} />
              <Input
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="What changed in this version? *"
                className="h-9"
              />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => { setShowUpload(false); setFile(null); setChangeNote(""); }}>Cancel</Button>
                <Button
                  size="sm"
                  className="flex-1 bg-paris-red hover:bg-paris-red/90"
                  onClick={handleVersionUpload}
                  disabled={!file || !changeNote.trim() || uploading}
                >
                  {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading…</> : "Version Update"}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowUpload(true)}>
              <Upload className="h-4 w-4 mr-1" />Version Update
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-14 rounded bg-muted animate-pulse" />)}
        </div>
      ) : versions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No versions yet.</p>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {versions.map(({ version, file: f, uploader }) => (
            <div
              key={version.id}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                version.status === "current"
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-border bg-muted/30"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">v{String(version.versionNumber).padStart(2, "0")}</span>
                  {version.status === "current" && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">Current</span>
                  )}
                  {version.status === "superseded" && !isAdmin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground flex items-center gap-0.5">
                      <Lock className="h-2.5 w-2.5" />Old
                    </span>
                  )}
                </div>
                {renamingVersionId === version.id ? (
                  <div className="flex items-center gap-1 mt-1">
                    <Input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRename(version.id);
                        if (e.key === "Escape") setRenamingVersionId(null);
                      }}
                      className="h-7 text-xs flex-1"
                      autoFocus
                    />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700" onClick={() => handleRename(version.id)}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" onClick={() => setRenamingVersionId(null)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {f?.fileName ?? "Unknown"}{f?.sizeBytes ? ` · ${formatSize(f.sizeBytes)}` : ""}
                    {uploader?.name ? ` · ${uploader.name}` : ""}
                    {` · ${new Date(version.uploadedAt).toLocaleDateString()}`}
                  </p>
                )}
                {version.changeNote && (
                  <p className="text-xs text-muted-foreground italic truncate">{version.changeNote}</p>
                )}
              </div>
              {(version.status === "current" || isAdmin) && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isAdmin && renamingVersionId !== version.id && (
                    <Button
                      variant="ghost" size="sm" className="h-8 w-8 p-0" title="Rename file"
                      onClick={() => { setRenamingVersionId(version.id); setRenameValue(f?.fileName ?? ""); }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View" onClick={() => fetchAndOpen(version.id, "view")}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Download" onClick={() => fetchAndOpen(version.id, "download")}>
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── AssignDocumentSheet ────────────────────────────────────────────────────
type TargetType = "all" | "role" | "job_title" | "division" | "user";

interface AssignTarget {
  type: TargetType;
  value?: string; // null/undefined for "all"
  label: string;
}

const ROLE_LABELS: Record<string, string> = {
  field_personnel: "Field Personnel",
  office_personnel: "Office Personnel",
  div_admin: "Division Admin",
  subcontractor: "Subcontractor",
  contractor: "Contractor",
};

// ── MultiSelectDropdown ─────────────────────────────────────────────────────
function MultiSelectDropdown({
  label,
  placeholder,
  options,
  selected,
  onToggle,
  searchable = false,
  disabled = false,
}: {
  label: string;
  placeholder: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string, label: string) => void;
  searchable?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const filtered = searchable && search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const selectedLabels = selected.map((v) => options.find((o) => o.value === v)?.label ?? v);

  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={`w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border text-sm transition-colors ${
            open ? "border-blue-500 ring-1 ring-blue-500/30" : "border-border hover:border-foreground/40"
          } ${disabled ? "opacity-50 cursor-not-allowed bg-muted" : "bg-background"}`}
        >
          <span className={selected.length > 0 ? "text-foreground" : "text-muted-foreground"}>
            {selected.length === 0
              ? placeholder
              : selected.length === options.length && options.length > 1
              ? `All (${selected.length})`
              : selected.length === 1
              ? selectedLabels[0]
              : `${selected.length} selected`}
          </span>
          <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
            {searchable && (
              <div className="p-1.5 border-b border-border">
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full px-2 py-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            <div className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">No results</p>
              ) : (
                filtered.map((opt) => {
                  const checked = selected.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onToggle(opt.value, opt.label)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                    >
                      <span className={`h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                        checked ? "bg-blue-600 border-blue-600" : "border-border"
                      }`}>
                        {checked && <Check className="h-2.5 w-2.5 text-white" />}
                      </span>
                      <span className={checked ? "font-medium" : ""}>{opt.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {selected.map((v) => {
            const lbl = options.find((o) => o.value === v)?.label ?? v;
            return (
              <span key={v} className="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-full pl-2.5 pr-1.5 py-0.5">
                {lbl}
                <button type="button" onClick={() => onToggle(v, lbl)} className="rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 p-0.5 transition-colors">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssignDocumentSheet({
  doc,
  isSuperAdmin,
  onDone,
  onCancel,
}: {
  doc: SafetyDoc;
  isSuperAdmin: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [targets, setTargets] = useState<AssignTarget[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [message, setMessage] = useState("");
  const [quizChoice, setQuizChoice] = useState<QuizChoice>(DEFAULT_QUIZ_CHOICE);
  const [allTeam, setAllTeam] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: divsData } = useQuery({
    queryKey: ["admin", "divisions"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/admin/divisions"),
  });
  const divisions = divsData?.data ?? [];

  const { data: jobTitlesData } = useQuery({
    queryKey: ["admin", "job-titles"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/admin/job-titles"),
  });
  const jobTitles = jobTitlesData?.data ?? [];

  const { data: usersData } = useQuery({
    queryKey: ["admin", "users-list"],
    queryFn: () => apiGet<{ data: { id: string; name: string; email: string; role: string | null; divisionId: string | null; divisionIds: string[]; jobTitleId: string | null; supervisedDivisionIds: string[] }[] }>("/api/users"),
  });
  const usersList = usersData?.data ?? [];

  // Derive selected values per type from targets
  const selectedRoles = targets.filter((t) => t.type === "role").map((t) => t.value!);
  const selectedJobTitles = targets.filter((t) => t.type === "job_title").map((t) => t.value!);
  const selectedDivisions = targets.filter((t) => t.type === "division").map((t) => t.value!);
  const selectedUsers = targets.filter((t) => t.type === "user").map((t) => t.value!);

  function handleAllTeamToggle() {
    if (allTeam) {
      setAllTeam(false);
      setTargets([]);
    } else {
      setAllTeam(true);
      setTargets([{ type: "all", label: "All Team" }]);
    }
  }

  function handleTypeToggle(type: Exclude<TargetType, "all">, value: string, label: string) {
    if (allTeam) { setAllTeam(false); }
    setTargets((prev) => {
      const withoutAll = prev.filter((p) => p.type !== "all");
      const key = `${type}:${value}`;
      const exists = withoutAll.some((p) => `${p.type}:${p.value}` === key);
      return exists
        ? withoutAll.filter((p) => `${p.type}:${p.value}` !== key)
        : [...withoutAll, { type, value, label }];
    });
  }

  const totalTargetCount = allTeam ? 1 :
    selectedRoles.length + selectedJobTitles.length + selectedDivisions.length + selectedUsers.length;

  async function handleSubmit() {
    if (totalTargetCount === 0) { toast.error("Select at least one target"); return; }
    if (!doc.currentVersionId) { toast.error("Document has no current version"); return; }
    setSaving(true);
    try {
      const finalTargets = allTeam
        ? [{ type: "all" as const }]
        : targets.map((t) => ({ type: t.type, ...(t.value != null ? { value: t.value } : {}) }));
      await apiPost("/api/admin/doc-assignments", {
        documentId: doc.id,
        versionId: doc.currentVersionId,
        targets: finalTargets,
        dueDate: dueDate || null,
        message: message || null,
        ...(({ questionCount, passScore, enableQuiz }) => ({ questionCount, passScore, enableQuiz }))(normalizeQuizChoice(quizChoice)),
      });
      onDone();
    } catch (e: any) {
      toast.error(e.message || "Failed to create assignment");
    } finally {
      setSaving(false);
    }
  }

  const roleOptions = Object.entries(ROLE_LABELS).map(([v, label]) => ({ value: v, label }));
  const jobTitleOptions = jobTitles.map((jt) => ({ value: jt.id, label: jt.name }));
  const divisionOptions = divisions.map((d) => ({ value: d.id, label: d.name }));
  // Filter users to only those matching ALL selected groups; always keep already-selected users
  const filteredUsers = usersList.filter((u) => {
    if (selectedUsers.includes(u.id)) return true;
    const hasGroupFilter = selectedRoles.length > 0 || selectedJobTitles.length > 0 || selectedDivisions.length > 0;
    if (!hasGroupFilter) return true;
    if (selectedRoles.length > 0 && !selectedRoles.includes(u.role ?? "")) return false;
    if (selectedJobTitles.length > 0 && !selectedJobTitles.includes(u.jobTitleId ?? "")) return false;
    if (selectedDivisions.length > 0) {
      const allUserDivs = [...(u.divisionIds ?? []), ...(u.supervisedDivisionIds ?? [])];
      if (!allUserDivs.some((d) => selectedDivisions.includes(d))) return false;
    }
    return true;
  });
  const userOptions = filteredUsers.map((u) => ({ value: u.id, label: u.name || u.email }));

  return (
    <Sheet open onOpenChange={(v) => !v && onCancel()}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle>Assign Document</SheetTitle>
          <p className="text-sm text-muted-foreground truncate">{doc.title}</p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-4 pr-1">
          {/* Assign To */}
          <div className="space-y-4">
            <Label className="text-xs font-semibold uppercase tracking-wide">Assign To</Label>

            {/* All Team toggle */}
            <button
              type="button"
              onClick={handleAllTeamToggle}
              className={`w-full flex items-center gap-3 h-10 px-3 rounded-md border text-sm transition-colors ${
                allTeam
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium"
                  : "border-border hover:border-foreground/40 text-muted-foreground"
              }`}
            >
              <span className={`h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                allTeam ? "bg-blue-600 border-blue-600" : "border-border"
              }`}>
                {allTeam && <Check className="h-2.5 w-2.5 text-white" />}
              </span>
              <span>Entire Company (All Team Members)</span>
            </button>

            <div className={`space-y-4 ${allTeam ? "opacity-40 pointer-events-none" : ""}`}>
              <MultiSelectDropdown
                label="By Role"
                placeholder="Select roles…"
                options={roleOptions}
                selected={selectedRoles}
                onToggle={(v, lbl) => handleTypeToggle("role", v, lbl)}
              />

              {jobTitles.length > 0 && (
                <MultiSelectDropdown
                  label="By Job Title"
                  placeholder="Select job titles…"
                  options={jobTitleOptions}
                  selected={selectedJobTitles}
                  onToggle={(v, lbl) => handleTypeToggle("job_title", v, lbl)}
                  searchable={jobTitles.length > 8}
                />
              )}

              {divisions.length > 0 && (
                <MultiSelectDropdown
                  label="By Division"
                  placeholder="Select divisions…"
                  options={divisionOptions}
                  selected={selectedDivisions}
                  onToggle={(v, lbl) => handleTypeToggle("division", v, lbl)}
                />
              )}

              {usersList.length > 0 && (
                <MultiSelectDropdown
                  label="Individual Users"
                  placeholder="Search and select users…"
                  options={userOptions}
                  selected={selectedUsers}
                  onToggle={(v, lbl) => handleTypeToggle("user", v, lbl)}
                  searchable
                />
              )}
            </div>

            {/* Summary */}
            {totalTargetCount > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2">
                <Users className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                  {allTeam
                    ? "Assigning to entire company"
                    : `${totalTargetCount} group${totalTargetCount !== 1 ? "s" : ""} selected`}
                </p>
              </div>
            )}
          </div>

          <QuizAssignmentSettings
            docs={[{ id: doc.id, title: doc.title }]}
            value={quizChoice}
            onChange={setQuizChoice}
          />

          {/* Due date */}
          <div className="space-y-1.5">
            <Label htmlFor="assign-due" className="text-xs font-semibold uppercase tracking-wide">
              Due Date <span className="text-muted-foreground font-normal normal-case">(optional)</span>
            </Label>
            <div className="relative">
              <CalendarIcon className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                id="assign-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="pl-8 text-sm"
              />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <Label htmlFor="assign-msg" className="text-xs font-semibold uppercase tracking-wide">
              Message <span className="text-muted-foreground font-normal normal-case">(optional)</span>
            </Label>
            <Textarea
              id="assign-msg"
              placeholder="Add a note for the assignees…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="text-sm resize-none"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSubmit} disabled={saving || totalTargetCount === 0}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Assign
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
